import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { fmtBulan, fmtPersen } from "@/lib/hpp";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GitCompare, Trophy, X, Plus } from "lucide-react";

const ROWS = [
  { k: "total_modal", l: "Total Modal Awal", f: rupiah, best: "min" },
  { k: "total_opex", l: "Biaya Tetap / Bulan", f: rupiah, best: "min" },
  { k: "hpp_dasar", l: "HPP Dasar / Unit", f: rupiah, best: "min" },
  { k: "hpp_total", l: "HPP Total / Unit", f: rupiah, best: "min" },
  { k: "rekomendasi_harga", l: "Rekomendasi Harga", f: rupiah },
  { k: "harga_aktual", l: "Harga Jual Aktual", f: rupiah },
  { k: "margin_aktual", l: "Margin Aktual", f: fmtPersen, best: "max" },
  { k: "laba_per_unit", l: "Laba / Unit", f: rupiah, best: "max" },
  { k: "omzet_bulanan", l: "Omzet / Bulan", f: rupiah, best: "max" },
  { k: "laba_bulanan", l: "Laba Kotor / Bulan", f: rupiah, best: "max" },
  { k: "bep_aktual_bulan", l: "BEP (Balik Modal)", f: (v) => (v == null ? "Tidak balik modal" : fmtBulan(v)), best: "min" },
];

export default function HppCompare() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [all, setAll] = useState([]);
  const ids = (sp.get("ids") || "").split(",").filter(Boolean).slice(0, 3);
  useEffect(() => { api.get("/hpp/simulations").then((r) => setAll(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  const sims = ids.map((id) => all.find((s) => s.id === id)).filter(Boolean);
  const setIds = (next) => setSp(next.length ? { ids: next.join(",") } : {});
  const bestOf = (row) => {
    if (!row.best || sims.length < 2) return null;
    const vals = sims.map((s) => s.hasil?.[row.k]).filter((v) => v !== null && v !== undefined);
    if (!vals.length) return null;
    return row.best === "min" ? Math.min(...vals) : Math.max(...vals);
  };
  const available = all.filter((s) => !ids.includes(s.id));

  return (
    <div className="p-4 md:p-8 space-y-4">
      <div>
        <h1 className="text-xl md:text-2xl font-semibold text-slate-900 flex items-center gap-2"><GitCompare size={22} className="brand-text" /> Bandingkan Simulasi</h1>
        <p className="text-xs md:text-sm text-slate-500">Pilih 2–3 simulasi. Nilai terbaik di tiap baris ditandai <Trophy size={12} className="inline text-amber-500" />.</p>
      </div>
      {ids.length < 3 && available.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="hpp-compare-add">
          {available.slice(0, 12).map((s) => (
            <button key={s.id} onClick={() => setIds([...ids, s.id])} className="text-xs px-3 py-2 rounded-sm border border-dashed border-slate-300 bg-white hover:border-[color:var(--brand)] inline-flex items-center gap-1" data-testid={`hpp-compare-add-${s.id}`}>
              <Plus size={12} /> {s.nama}
            </button>
          ))}
        </div>
      )}
      {sims.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-slate-500" data-testid="hpp-compare-empty">
          Belum ada simulasi dipilih. {all.length < 2 ? "Buat minimal 2 simulasi terlebih dahulu." : "Klik nama simulasi di atas untuk menambahkan."}
          <div className="mt-3"><Button variant="outline" onClick={() => nav("/hpp")} className="rounded-sm">Ke Daftar Simulasi</Button></div>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto" data-testid="hpp-compare-table">
          <table className="w-full text-xs md:text-sm min-w-[520px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="p-3 text-left text-[11px] uppercase tracking-wide text-slate-500 sticky left-0 bg-slate-50 w-40">Metrik</th>
                {sims.map((s) => (
                  <th key={s.id} className="p-3 text-left align-top">
                    <div className="flex items-start justify-between gap-2">
                      <button onClick={() => nav(`/hpp/${s.id}`)} className="text-left font-semibold text-slate-900 hover:underline">{s.nama}</button>
                      <button onClick={() => setIds(ids.filter((x) => x !== s.id))} className="text-slate-400 hover:text-rose-600 p-0.5" aria-label="Keluarkan" data-testid={`hpp-compare-remove-${s.id}`}><X size={14} /></button>
                    </div>
                    <div className="text-[11px] font-normal text-slate-500">{s.kategori_nama || "Custom"} · {fmtPersen(s.margin_persen)} · {Number(s.volume || 0).toLocaleString("id-ID")} unit/bln</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => {
                const best = bestOf(row);
                return (
                  <tr key={row.k} className="border-b border-slate-100">
                    <td className="p-3 text-slate-600 sticky left-0 bg-white">{row.l}</td>
                    {sims.map((s) => {
                      const v = s.hasil?.[row.k];
                      const isBest = best !== null && v === best;
                      return (
                        <td key={s.id} className={`p-3 font-mono ${isBest ? "text-emerald-700 font-semibold bg-emerald-50/50" : ""}`} data-testid={`hpp-cmp-${row.k}-${s.id}`}>
                          {row.f(v)} {isBest && <Trophy size={12} className="inline text-amber-500 -mt-0.5" />}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
